import React, { useState, useRef, useEffect } from 'react';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useArticleTags, useCreateArticleTag } from '@/hooks/useArticles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Plus, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { ArticleTag } from '@/types/articles';

interface ArticleTagSelectorProps {
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
}

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const ArticleTagSelector: React.FC<ArticleTagSelectorProps> = ({
  selectedTagIds,
  onTagsChange,
}) => {
  const { currentBrandId: selectedBrandId } = useBrandManager();
  const { data: tags = [] } = useArticleTags(selectedBrandId);
  const createTag = useCreateArticleTag();

  const [isAdding, setIsAdding] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const selectedTags = tags.filter(t => selectedTagIds.includes(t.id));
  const availableTags = tags.filter(t => !selectedTagIds.includes(t.id));

  const handleToggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagsChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onTagsChange([...selectedTagIds, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!selectedBrandId || !newTagName.trim()) return;

    // Check if tag already exists
    const existingTag = tags.find(t => t.name.toLowerCase() === newTagName.trim().toLowerCase());
    if (existingTag) {
      if (!selectedTagIds.includes(existingTag.id)) {
        onTagsChange([...selectedTagIds, existingTag.id]);
      }
      setNewTagName('');
      setIsAdding(false);
      return;
    }

    setIsCreating(true);
    try {
      const slug = newTagName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      
      const newTag = await createTag.mutateAsync({
        brand_id: selectedBrandId,
        name: newTagName.trim(),
        slug,
        color,
      });

      onTagsChange([...selectedTagIds, newTag.id]);
      setNewTagName('');
      setIsAdding(false);
      toast.success('Tag created');
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewTagName('');
    }
  };

  return (
    <div className="space-y-3">
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="pl-2 pr-1 py-1 flex items-center gap-1"
              style={{ backgroundColor: `${tag.color}20`, borderColor: tag.color }}
            >
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
              <span>{tag.name}</span>
              <button
                type="button"
                onClick={() => handleToggleTag(tag.id)}
                className="ml-1 hover:bg-black/10 rounded p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Available Tags */}
      {availableTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleToggleTag(tag.id)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed hover:border-solid hover:bg-muted/50 transition-colors text-sm"
            >
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
              <span>{tag.name}</span>
              <Plus className="h-3 w-3 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      {/* Add New Tag */}
      {isAdding ? (
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            placeholder="New tag name..."
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 text-sm"
            disabled={isCreating}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || isCreating}
            className="h-8 w-8 p-0"
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setIsAdding(false); setNewTagName(''); }}
            className="h-8 w-8 p-0"
            disabled={isCreating}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsAdding(true)}
          className="h-8 text-muted-foreground"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add tag
        </Button>
      )}
    </div>
  );
};

export default ArticleTagSelector;
